import { useMemo, useState } from "react";
import { Spin } from "antd";
import styled from "@emotion/styled";
import { DownOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { CalendarOutlined } from "@ant-design/icons";
import { CYCLE_COLORS } from "../../../utils/Service/colors";
import CycleBar from "../../../components/common/CycleBar";
import CardHeader from "../../../components/common/CardHeader";

const extractYesNo = (options = []) => {
  const yes = options.find(
    (o) => (o.option_name || "").toLowerCase() === "yes",
  );
  const no = options.find((o) => (o.option_name || "").toLowerCase() === "no");

  if (yes || no) {
    return [
      { label: "Yes", percentage: yes?.percentage ?? 0, tone: "positive" },
      { label: "No", percentage: no?.percentage ?? 0, tone: "negative" },
    ];
  }

  return options.slice(0, 2).map((o, i) => ({
    label: o.option_name || "N/A",
    percentage: o.percentage ?? 0,
    tone: i === 0 ? "positive" : "negative",
  }));
};

const QuestionSummary = ({
  cycleIds,
  cycles,
  qsLoading,
  data,
  downloading,
  downloadQuestionSummaryTable,
}) => {
  const [collapsedSections, setCollapsedSections] = useState(new Set());

  const columnNameById = useMemo(() => {
    const map = {};
    (data?.columns || []).forEach((col) => {
      map[col.audit_cycle_id] = col.audit_cycle_name;
    });
    return map;
  }, [data]);

  const normalizedCycles = useMemo(() => {
    const selectedIds = new Set(cycleIds || []);

    return (cycles || [])
      .filter((entry) => {
        const id =
          entry && typeof entry === "object"
            ? (entry.id ?? entry.audit_cycle_id)
            : entry;

        return selectedIds.has(id);
      })
      .map((entry) => {
        if (entry && typeof entry === "object") {
          const id = entry.id ?? entry.audit_cycle_id;

          return {
            id,
            name:
              entry.name ?? entry.audit_cycle_name ?? columnNameById[id] ?? id,
          };
        }

        return {
          id: entry,
          name: columnNameById[entry] ?? entry,
        };
      });
  }, [cycles, cycleIds, columnNameById]);

  const cycleColor = (cycleId) => {
    const idx = normalizedCycles.findIndex((c) => c.id === cycleId);
    return CYCLE_COLORS[idx % CYCLE_COLORS.length] || CYCLE_COLORS[0];
  };

  const mergedSections = useMemo(() => {
    if (!data?.sections) return [];

    return data.sections.map((section) => ({
      section: section.section_name,
      questions: (section.questions || []).map((question) => {
        const perCycle = normalizedCycles.map((cycle) => {
          const cycleEntry = (question.cycles || []).find(
            (c) => c.audit_cycle_id === cycle.id,
          );

          return {
            cycleId: cycle.id,
            values: cycleEntry ? extractYesNo(cycleEntry.options) : [],
          };
        });

        return {
          question_id: question.question_id,
          question_txt: question.question_txt,
          perCycle,
        };
      }),
    }));
  }, [data, normalizedCycles]);

  const sectionAverage = (section, cycleId) => {
    const values = section.questions
      .flatMap(
        (q) => q.perCycle.find((c) => c.cycleId === cycleId)?.values || [],
      )
      .filter((v) => v.tone === "positive");

    if (values.length === 0) return null;

    const avg =
      values.reduce((sum, v) => sum + (Number(v.percentage) || 0), 0) /
      values.length;

    return Math.floor(avg.toFixed(1));
  };

  const toggleSection = (sectionIndex) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionIndex)) next.delete(sectionIndex);
      else next.add(sectionIndex);
      return next;
    });
  };

  const cycleCount = normalizedCycles.length;
  const questionWidth =
    cycleCount === 1 ? 50 : cycleCount === 2 ? 40 : cycleCount === 3 ? 30 : 25;
  const serialNoWisth = cycleCount > 3 ? "3%" : cycleCount > 1 ? "5%" : "6%";
  const cycleWidth = (100 - 6 - questionWidth) / cycleCount;

  return (
    <Card className="dashboard-card shadow p-3 gap-0">
      <CardHeader
        title="Questionnaire Summary"
        subtitle="Percentage represents % of 'Yes' responses"
        downloading={downloading}
        onDownloadClick={() =>
          downloadQuestionSummaryTable("questionnaire-summary")
        }
      />

      <div className="d-flex justify-content-between w-100">
        <CycleBar cycleIds={cycleIds} cycles={cycles} />
        <Legend>
          <LegendItem>
            <LegendSwatch tone="positive" /> Yes (%)
          </LegendItem>
          <LegendItem>
            <LegendSwatch tone="negative" /> No (%)
          </LegendItem>
        </Legend>
      </div>

      {/* ================= CONTENT ================= */}
      <Body>
        {normalizedCycles.length === 0 ? (
          <EmptyMessage>
            Select a cycle to view questionnaire summary
          </EmptyMessage>
        ) : qsLoading ? (
          <div style={{ padding: "2rem 0", textAlign: "center" }}>
            <Spin />
          </div>
        ) : mergedSections.length === 0 ? (
          <EmptyMessage>No question summary available</EmptyMessage>
        ) : (
          mergedSections.map((section, sectionIndex) => {
            const isCollapsed = collapsedSections.has(sectionIndex);

            return (
              <SectionBlock key={sectionIndex}>
                <SectionHeader
                  key={sectionIndex}
                  onClick={() => toggleSection(sectionIndex)}
                >
                  <p style={{ margin: 0 }}>Section - {section.section}</p>
                  <DownOutlined
                    style={{
                      transition: "transform 0.2s ease",
                      transform: isCollapsed
                        ? "rotate(-90deg)"
                        : "rotate(0deg)",
                    }}
                  />
                </SectionHeader>

                {!isCollapsed && (
                  <TableWrap>
                    <Table>
                      <thead>
                        <tr>
                          <Th
                            style={{
                              width: serialNoWisth,
                              textAlign: "center",
                              padding: "2px",
                            }}
                          >
                            Sr. No.
                          </Th>

                          <CycleTh
                            style={{
                              width: `${questionWidth}%`,
                            }}
                            borderColor="#cccccc"
                          >
                            Question
                          </CycleTh>

                          {normalizedCycles.map((cycle) => {
                            const color = cycleColor(cycle.id);

                            return (
                              <CycleTh
                                key={cycle.id}
                                color={color.bg}
                                borderColor={color.main}
                                style={{
                                  width: `${cycleWidth}%`,
                                }}
                              >
                                <CycleHeaderBadge color={color.main}>
                                  <CalendarOutlined
                                    style={{
                                      fontSize: "1rem",
                                      marginRight: "4px",
                                      flexShrink: 0,
                                    }}
                                  />
                                  {cycle.name}
                                </CycleHeaderBadge>
                              </CycleTh>
                            );
                          })}
                        </tr>
                      </thead>

                      <>
                        <tbody>
                          {section.questions.length === 0 ? (
                            <tr>
                              <td colSpan={2 + normalizedCycles.length}>
                                <EmptyMessage>
                                  No question summary available for this section
                                </EmptyMessage>
                              </td>
                            </tr>
                          ) : (
                            section.questions.map((question, qIndex) => (
                              <Tr
                                key={question.question_id}
                                striped={qIndex % 2 !== 0}
                              >
                                <Td center>{qIndex + 1}</Td>
                                <Td left>{question.question_txt}</Td>

                                {normalizedCycles.map((cycle) => {
                                  const cycleValues =
                                    question.perCycle.find(
                                      (c) => c.cycleId === cycle.id,
                                    )?.values || [];

                                  return (
                                    <CycleTd key={cycle.id}>
                                      {cycleValues.length === 0 ? (
                                        <NoDataCell>—</NoDataCell>
                                      ) : (
                                        cycleValues.map((v, i) => (
                                          <PillRow key={i}>
                                            <PillLabel>{v.label}</PillLabel>
                                            <Pill tone={v.tone}>
                                              {Math.floor(v.percentage)}%
                                            </Pill>
                                          </PillRow>
                                        ))
                                      )}
                                    </CycleTd>
                                  );
                                })}
                              </Tr>
                            ))
                          )}
                        </tbody>
                        {section.questions.length > 0 && (
                          <tfoot>
                            <tr>
                              <AvgTd colSpan={2}>SECTION AVERAGE</AvgTd>
                              {normalizedCycles.map((cycle) => {
                                const avg = sectionAverage(section, cycle.id);
                                return (
                                  <AvgTd key={cycle.id}>
                                    {avg !== null ? (
                                      <Pill
                                        tone="positive"
                                        style={{ fontSize: "0.95rem" }}
                                      >
                                        {avg}%
                                      </Pill>
                                    ) : (
                                      "—"
                                    )}
                                  </AvgTd>
                                );
                              })}
                            </tr>
                          </tfoot>
                        )}
                      </>
                    </Table>
                  </TableWrap>
                )}
              </SectionBlock>
            );
          })
        )}

        {mergedSections.length > 0 && !qsLoading && (
          <>
            <NoteBar>
              <InfoCircleOutlined
                style={{ marginRight: 6, color: "#5B8FBA" }}
              />
              <strong>Note:</strong>&nbsp;Percentage indicates the proportion of
              'Yes' responses for each question in the selected cycles.
            </NoteBar>
          </>
        )}
      </Body>
    </Card>
  );
};

/* ============================= STYLES ============================= */

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e9edf1;
`;

const Body = styled.div`
  margin-top: 12px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 24px 0;
  color: #8a94a6;
  font-size: 13px;
`;

const NoDataCell = styled.div`
  text-align: center;
  color: #b7bec9;
  font-size: 12px;
  padding: 4px 0;
`;

const SectionBlock = styled.div`
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  cursor: pointer;
  width: "100%";
  background-color: #007dc1;
  height: 3rem;
  text-align: start;
  padding: 1rem;
  color: white;
  border-top-left-radius: 0.5rem !important;
  border-top-right-radius: 0.5rem !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  align-self: center;
  color: #fff;
  font-weight: 500;
  font-size: 1.1rem !important;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid #e9edf1;
  border-radius: 10px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
`;

const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
`;

const Th = styled.th`
  border-top: 3px solid #cccccc !important;
  background: #fafafa !important;
  color: #003c5d !important;
  font-size: 1rem;
  font-weight: 600;
  padding: 12px 14px;
  text-align: center;
`;

const CycleTh = styled.th`
  padding: 10px 8px;
  text-align: center;
  white-space: normal;
  overflow: hidden;
  background-color: ${(props) => props.color} !important;
  border-top: 3px solid ${(props) => props.borderColor} !important;
`;

const CycleHeaderBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-width: 0;

  padding: 4px 6px;
  color: ${(props) => props.color};
  font-size: 0.9rem;
  font-weight: 700;

  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const Tr = styled.tr`
  background: ${(props) => (props.striped ? "rgb(242,242,242)" : "#ffffff")};
  transition: background-color 0.15s ease;
  &:hover {
    background: ${(props) => (props.striped ? "#ffffff" : "rgb(242,242,242)")};
  }
`;

const Td = styled.td`
  padding-left: ${(props) => (props.left ? "1rem" : "0rem")} !important;
  font-size: 13px;
  color: #353e4c;
  text-align: ${(props) => (props.center ? "center" : "left")};
  vertical-align: left;
`;

const CycleTd = styled.td`
  padding: 0 2rem !important;
  font-size: 13px;
  color: #353e4c;
  text-align: ${(props) => (props.center ? "center" : "left")};
  vertical-align: center;
`;

const AvgTd = styled.td`
  padding: 0.5rem !important;
  font-size: 12px;
  font-weight: 700;
  color: #0f2a3d;
  text-align: center;
  background: #eef6fd;
`;

const PillRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 3px 0;
`;

const PillLabel = styled.span`
  font-size: 12px;
  color: #556072;
  font-weight: 600;
`;

const Pill = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 4px;
  width: 3rem;
  color: ${(props) => (props.tone === "negative" ? "#B42318" : "#166534")};
  background: ${(props) => (props.tone === "negative" ? "#FAD1CE" : "#C6F2D5")};
  align-items: center;
  text-align: center;
`;

const Legend = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin: 8px 2rem 0 1rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #353e4c;
`;

const LegendSwatch = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;
  background: ${(props) => (props.tone === "negative" ? "#f87171" : "#86efac")};
`;

const NoteBar = styled.div`
  display: flex;
  align-items: center;
  background: #eef6fd;
  color: #45536a;
  font-size: 12px;
  padding: 10px 14px;
  border-radius: 8px;
`;

export default QuestionSummary;
